<template>
  <button
    :class="buttonClass"
    :style="buttonStyle"
    :disabled="disabled"
    @click="onClick"
  >
    <slot />
  </button>
</template>

<script>
export default {
  name: "Button",
  props: {
    type: {
      type: String,
      default: "primary",
      validator: (value) =>
        ["primary", "outline", "secondary", "ghost"].includes(value),
    },
    size: {
      type: String,
      default: "md",
      validator: (value) => ["sm", "md", "lg"].includes(value),
    },
    borderRadius: {
      type: [String, Number],
      default: "4px",
    },
    height: {
      type: [String, Number],
      default: "auto",
    },
    width: {
      type: [String, Number],
      default: "auto",
    },
    rounded: {
      type: Boolean,
      default: false,
    },
    shadow: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    color: {
      type: String,
      default: "#0B43F1",
    },
    textColor: {
      type: String,
      default: "#0B43F1",
    },
    borderColor: {
      type: String,
      default: "#0B43F1",
    },
  },
  computed: {
    buttonClass() {
      return [
        `btn-${this.type}`,
        `btn-${this.size}`,
        { "btn-rounded": this.rounded },
        { "btn-shadow": this.shadow },
        { "btn-disabled": this.disabled },
      ];
    },
    buttonStyle() {
      return {
        "--button-color": this.type === "outline" ? "transparent" : this.color,
        "--text-color": this.textColor,
        "--border-color": this.borderColor,
        borderRadius:
          typeof this.borderRadius === "number"
            ? `${this.borderRadius}px`
            : this.borderRadius,
        height:
          typeof this.height === "number" ? `${this.height}px` : this.height,
        width: typeof this.width === "number" ? `${this.width}px` : this.width,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: this.disabled ? "not-allowed" : "pointer",
        opacity: this.disabled ? 0.6 : 1,
      };
    },
  },
  methods: {
    onClick(event) {
      if (!this.disabled) this.$emit("click", event);
    },
  },
};
</script>

<style scoped>
button {
  border: 2px solid var(--border-color, transparent);
  background-color: var(--button-color, #0b43f1);
  color: var(--text-color, #ffffff);
  font-family: "Inter", sans-serif;
  font-weight: 500;
  text-align: center;
  display: inline-block;
  padding: 0.6em 1.2em;
  transition: all 0.3s ease;
  outline: none;
  user-select: none;
}

.btn-primary {
  --button-color: #0b43f1;
  --text-color: #ffffff;
  --border-color: transparent;
}

.btn-outline {
  --button-color: transparent;
  --text-color: #0b43f1;
  --border-color: #0b43f1;
}

button:not(.btn-disabled):hover {
  box-shadow: 0px 1px 8px rgba(0, 0, 0, 0.25);
}

button:not(.btn-disabled):active {
  background-color: #0b43f1;
  color: #ffffff;
}

.btn-sm {
  font-size: 14px;
  padding: 0.4em 0.8em;
}

.btn-md {
  font-size: 16px;
  padding: 0.6em 1.2em;
}

.btn-lg {
  font-size: 20px;
  padding: 0.8em 1.6em;
}

.btn-rounded {
  border-radius: 50px;
}

.btn-shadow {
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.2);
}

.btn-disabled {
  pointer-events: none;
  background-color: #f0f0f0;
  color: #b0b0b0;
  border-color: #d0d0d0;
}
</style>
